import React, { useState, useEffect, useCallback } from 'react';
import { evaluate } from 'mathjs';
import 'bootstrap/dist/css/bootstrap.min.css';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';
import Alert from 'react-bootstrap/Alert';

const PipeVelocityCalculator = () => {
    const [flow, setFlow] = useState('');
    const [pipeSize, setPipeSize] = useState('1/2 in');
    const [pipeId, setPipeId] = useState('0.622');
    const [velocity, setVelocity] = useState('');
    const [velocityStatus, setVelocityStatus] = useState('');
    
    const pipeSizes = [
        { nominal: '1/2 in', actual: 0.622, type: 'Schedule 40' },
        { nominal: '3/4 in', actual: 0.824, type: 'Schedule 40' },
        { nominal: '1 in', actual: 1.049, type: 'Schedule 40' },
        { nominal: '1 1/4 in', actual: 1.38, type: 'Schedule 40' },
        { nominal: '1 1/2 in', actual: 1.61, type: 'Schedule 40' },
        { nominal: '2 in', actual: 2.067, type: 'Schedule 40' },
        { nominal: '2 1/2 in', actual: 2.469, type: 'Schedule 40' },
        { nominal: '3 in', actual: 3.068, type: 'Schedule 40' },
        { nominal: '4 in', actual: 4.026, type: 'Schedule 40' },
        { nominal: '5 in', actual: 5.047, type: 'Schedule 40' },
        { nominal: '6 in', actual: 6.065, type: 'Schedule 40' },
        { nominal: '8 in', actual: 7.981, type: 'Schedule 40' },
        { nominal: '10 in', actual: 10.02, type: 'Schedule 40' },
        { nominal: '12 in', actual: 12, type: 'Standard' },
        { nominal: '14 in', actual: 13.25, type: 'Standard' },
        { nominal: '16 in', actual: 15.25, type: 'Standard' },
        { nominal: '18 in', actual: 17.25, type: 'Standard' },
        { nominal: '20 in', actual: 19.25, type: 'Standard' },
        { nominal: '24 in', actual: 23.25, type: 'Standard' },
        { nominal: '30 in', actual: 29.25, type: 'Standard' },
    ];

    // Maximum velocity guidelines
    const velocityGuidelines = [
        { 
            type: "Flooded/Lift intake (up to, and including manifold)",
            description: "Any plumbing upstream of pump(s) on a flooded suction or suction lift system.",
            maxVelocity: 5 
        },
        { 
            type: "Suction branch (Flooded/Lift - manifold to pump)",
            description: "Only applies to branches between the intake manifold and pump!",
            maxVelocity: 7.5 
        },
        { 
            type: "Discharge manifold/elbow",
            description: "Any manifold or elbow downstream of pump(s) - includes discharge, filter, and vertical manifolds, drop pipes, etc.",
            maxVelocity: 10 
        },
        { 
            type: "Discharge pipe",
            description: "Any plumbing downstream of pump(s) - not manifolds or elbows.",
            maxVelocity: 15 
        }
    ];

    const calculateVelocity = useCallback(() => {
        try {
            if (!flow || !pipeId || isNaN(flow) || isNaN(pipeId)) {
                setVelocity('ENTER FLOW & PIPE DIAMETER');
                setVelocityStatus('');
                return;
            }
            
            // Formula: (flow/449) / (((pipeId/2)^2 * π) / 144)
            // Where: flow in gpm, pipeId in inches, result in feet per second
            // Division by 449 converts GPM to cubic feet per second
            // Division by 144 converts square inches to square feet
            
            const result = evaluate(`(${flow}/449)/(((${pipeId}/2)^2*pi)/144)`);
            setVelocity(result.toFixed(2));
            
            // Set status based on guidelines
            if (result <= 5) {
                setVelocityStatus('success');
            } else if (result <= 7.5) {
                setVelocityStatus('success');
            } else if (result <= 10) {
                setVelocityStatus('warning');
            } else if (result <= 15) {
                setVelocityStatus('warning');
            } else {
                setVelocityStatus('danger');
            }
            
        } catch (error) {
            console.error('Calculation error:', error);
            setVelocity('Error');
            setVelocityStatus('danger');
        }
    }, [flow, pipeId]);

    useEffect(() => {
        calculateVelocity();
    }, [calculateVelocity]);

    const handlePipeSizeChange = (e) => {
        const selectedSize = e.target.value;
        setPipeSize(selectedSize);
        
        // Find the actual ID for the selected pipe size
        const pipe = pipeSizes.find(p => p.nominal === selectedSize);
        if (pipe) {
            setPipeId(pipe.actual.toString());
        }
    };

    const getStatusClass = () => {
        switch(velocityStatus) {
            case 'success':
                return 'bg-success text-white';
            case 'warning':
                return 'bg-warning';
            case 'danger':
                return 'bg-danger text-white';
            default:
                return 'bg-light';
        }
    };

    return (
        <div className="container-fluid py-3">
            <Row className="mb-4">
                <Col lg={{span: 8, offset: 2}} md={12}>
                    <Card className="shadow-sm">
                        <Card.Header as="h5" className="bg-primary text-white">
                            Pipe Velocity Calculator
                        </Card.Header>
                        <Card.Body>
                            <Form>
                                <Row>
                                    <Col md={8}>
                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="flow"><strong>Flow Rate (GPM):</strong></Form.Label>
                                            <Form.Control
                                                type="number"
                                                id="flow"
                                                value={flow}
                                                onChange={(e) => setFlow(e.target.value)}
                                                placeholder="Enter flow rate"
                                                className="shadow-sm"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                
                                <Row className="mb-3">
                                    <Col md={5}>
                                        <Form.Group>
                                            <Form.Label htmlFor="pipeSize"><strong>Nominal Pipe Size:</strong></Form.Label>
                                            <Form.Select
                                                id="pipeSize"
                                                value={pipeSize}
                                                onChange={handlePipeSizeChange}
                                                className="shadow-sm"
                                            >
                                                {pipeSizes.map((size, index) => (
                                                    <option key={index} value={size.nominal}>
                                                        {size.nominal} ({size.type})
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label htmlFor="pipeId"><strong>Actual Inside Diameter (in):</strong></Form.Label>
                                            <Form.Control
                                                type="text"
                                                id="pipeId"
                                                value={pipeId}
                                                disabled
                                                className="bg-light"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Form>
                            
                            <div className="result-container text-center mt-4 p-3 border rounded">
                                <h5>Velocity Result:</h5>
                                {velocity !== 'ENTER FLOW & PIPE DIAMETER' && velocity !== 'Error' ? (
                                    <div className={`velocity-result p-3 rounded ${getStatusClass()}`}>
                                        <h3 className="mb-0">{velocity} ft/s</h3>
                                    </div>
                                ) : (
                                    <Alert variant="secondary">
                                        {velocity}
                                    </Alert>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            
            <Row>
                <Col lg={{span: 8, offset: 2}} md={12}>
                    <Card className="shadow-sm">
                        <Card.Header as="h5" className="bg-primary text-white">
                            Maximum Velocity Guidelines
                        </Card.Header>
                        <Card.Body>
                            <Table striped bordered hover responsive>
                                <thead className="bg-light">
                                    <tr>
                                        <th width="30%">Pipe Section</th>
                                        <th>Description</th>
                                        <th width="15%">Max Velocity</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {velocityGuidelines.map((guideline, index) => (
                                        <tr key={index}>
                                            <td><strong>{guideline.type}</strong></td>
                                            <td>{guideline.description}</td>
                                            <td className="text-center"><strong>{guideline.maxVelocity} fps</strong></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            <Alert variant="info" className="mt-3">
                                <i className="bi bi-info-circle me-2"></i>
                                These guidelines help ensure proper system performance and minimize issues like cavitation, erosion, and excessive noise.
                            </Alert>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default PipeVelocityCalculator;